/**
 * Where a learner's reading progress lives on the device.
 *
 * The rules are all in `#/lib/flashbookProgress`, which is pure; this is only the
 * door to storage. It is a separate module rather than a section at the bottom of
 * that one for a reason worth stating, because it looks like a fussy split until
 * you know it: the browser layout tests import the fixture module to get the book
 * they measure, and the fixture module builds a seeded history — so anything the
 * fixture reaches has to be loadable *outside* the app, where there is no device
 * storage to import. Keeping the pure model importable from both places is what
 * makes a seeded history possible at all.
 *
 * Besides storage it is one bell: a write is announced to whoever is drawing a
 * place, so a surface that is not the reader can follow along (see
 * `subscribeFlashBookProgress`).
 */
import {BOOK_LIMIT, type FlashBookReadProgress} from '#/lib/flashbookProgress'
import {device} from '#/storage'

/**
 * Every book's progress on this device, keyed by the book's record URI.
 *
 * Keyed by the *record* rather than by the post it was found in, because a book is
 * read by opening the book: the same book reached from a different post, a profile
 * or a message is the same read, and progress that failed to follow it would offer
 * to resume a book the learner is halfway through.
 */
export type FlashBookProgressIndex = Record<string, FlashBookReadProgress>

export function readProgressIndex(): FlashBookProgressIndex {
  return device.get(['flashbookProgress']) ?? {}
}

/**
 * How many times a place has been written since the app started.
 *
 * The store's change signal, and a *number* deliberately: a surface that follows the
 * store — React's `useSyncExternalStore`, via
 * `#/components/flashbooks/useFlashBookPlace` — needs a snapshot it can compare, and
 * a parsed index is a fresh object on every read, so a card that subscribed to one
 * would re-render for ever. A counter is stable between writes and tells a
 * subscriber everything it needs: that something changed. What changed is read from
 * storage, which is the only copy of the truth.
 */
let writes = 0

/** The store's version, for a subscriber that re-reads when it moves. */
export function flashBookProgressVersion(): number {
  return writes
}

/**
 * The index as of a version, remembered rather than re-read.
 *
 * A feed draws thirty cards at once and every one of them asks the same question in
 * the same render pass, while a parsed index is a fresh object on every read: without
 * this, drawing one screen would be thirty storage reads and thirty new snapshots for
 * `useSyncExternalStore` to compare. Keyed by the version, which is the store's own
 * answer to "has anything changed" — so the only way to read a stale copy is to read
 * it between a write and the notification that follows the write, which is inside one
 * call and not something a surface can observe.
 */
let indexCache: {version: number; index: FlashBookProgressIndex} | null = null

/** Every book's progress on this device, as of the version asked for. */
export function progressIndexAt(version: number): FlashBookProgressIndex {
  if (!indexCache || indexCache.version !== version) {
    indexCache = {version, index: readProgressIndex()}
  }
  return indexCache.index
}

export function getFlashBookProgress(
  bookUri: string,
): FlashBookReadProgress | null {
  return readProgressIndex()[bookUri] ?? null
}

/**
 * Write one book's progress, and keep the index bounded.
 *
 * Trimmed here rather than on a timer: the only moment the index can grow is when
 * something is written to it, so this is the one place that has to care. Dropping
 * a book's progress is a real loss — it is a read the learner cannot resume — so
 * the cut is by *last read*, which makes the book that disappears the one they
 * have not opened in the longest time.
 *
 * Every write is also announced (see `subscribeFlashBookProgress`), because a
 * surface that *draws* a place has no other way to hear about one.
 */
export function putFlashBookProgress(progress: FlashBookReadProgress): void {
  const index = {...readProgressIndex(), [progress.bookUri]: progress}
  const uris = Object.keys(index)
  let next = index
  if (uris.length > BOOK_LIMIT) {
    next = {}
    for (const uri of uris
      .sort(
        (a, b) =>
          Date.parse(index[b].updatedAt) - Date.parse(index[a].updatedAt),
      )
      .slice(0, BOOK_LIMIT)) {
      next[uri] = index[uri]
    }
  }
  device.set(['flashbookProgress'], next)
  // The version moves before anyone is told, so a listener that compares it during
  // its own notification sees the write it is being told about rather than the one
  // before it.
  writes += 1
  for (const listener of listeners) listener()
}

/**
 * Everyone watching the device's places.
 *
 * A place is written while the *reader* is on screen and drawn while the *feed* is,
 * so a surface like a feed card has to hear about a write it did not make: a learner
 * who reads three pages and comes back must find the card they left saying
 * "Continue" rather than "Read the book". Until now the only moment a surface
 * re-read the store was navigation focus, which the card has none of — each row in a
 * feed is a stranger to the screen's lifecycle — and which the shelf leans on.
 *
 * Listeners are told *that* something changed, and re-read the index themselves,
 * rather than being handed a value — the write path should not have to know what any
 * surface needs. They must not write: a listener that saved would be a loop.
 */
const listeners = new Set<() => void>()

/**
 * Watch every write to the device's places, for a surface that draws one.
 *
 * Returns its own unsubscribe, so it can be handed straight back from an effect.
 */
export function subscribeFlashBookProgress(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
